"use client";
import React, { useEffect, useMemo, useState } from "react";
import { MdCancel } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import AddDatasetLogic from "./AddDatasetLogic";
import "./AddDataset.css";
import { STATE_OPTIONS } from "../StateOptions";
import { FaEye, FaPlus } from "react-icons/fa";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { RiDeleteBin3Fill } from "react-icons/ri";

function AddDataset({ onCancel, onSave }) {
  const [acroFieldsList, setAcroFieldsList] = useState([]);
  const {
    datasetID,
    datasetName,
    datasetDisplayName,
    state,
    entryType,
    objectType,
    zohoDepartmentID,
    replyToEmail,
    datasetOPRARequestSendEnable,
    smtpFromEmail,
    smtpSubject,
    // fromRange,
    // toRange,
    errors,
    handleChange,
    handleSubmit,
    setUniversalFormFile,
    dailyEntryLimit,
    qaCompletionDays,
    ignoredFileApprovalDays,
  } = AddDatasetLogic({ onCancel, onSave, acroFieldsList });

  const [showThresholds, setShowThresholds] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("No file chosen");
  const [uploadedFileURL, setUploadedFileURL] = useState(null);
  const [showAcroFields, setShowAcroFields] = useState(false);
  const [acroFieldsTab, setAcroFieldsTab] = useState("json");
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState("");


  useEffect(() => {
    const selectedState = STATE_OPTIONS.find(
      (stateOption) => stateOption.value === state
    );

    setStateSearch(selectedState ? selectedState.label : "");
  }, [state]);

  const filteredStateOptions = useMemo(() => {
    const normalizedSearch = stateSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return STATE_OPTIONS;
    }

    return STATE_OPTIONS.filter((stateOption) => {
      const normalizedLabel = stateOption.label.toLowerCase();
      const normalizedValue = stateOption.value.toLowerCase();

      return (
        normalizedLabel.includes(normalizedSearch) ||
        normalizedValue.includes(normalizedSearch)
      );
    });
  }, [stateSearch]);

  const updateSelectedState = (value) => {
    handleChange({
      target: {
        name: "state",
        value,
      },
    });
  };

  const handleStateInputChange = (e) => {
    const inputValue = e.target.value;

    setStateSearch(inputValue);
    setIsStateDropdownOpen(true);

    updateSelectedState("");
  };

  const handleStateSelect = (stateOption) => {
    setStateSearch(stateOption.label);
    updateSelectedState(stateOption.value);
    setIsStateDropdownOpen(false);
  };

  const handleStateBlur = () => {
    window.setTimeout(() => {
      setIsStateDropdownOpen(false);

      const normalizedSearch = stateSearch.trim().toLowerCase();
      const exactMatch = STATE_OPTIONS.find((stateOption) => {
        const normalizedLabel = stateOption.label.toLowerCase();
        const normalizedValue = stateOption.value.toLowerCase();

        return (
          normalizedLabel === normalizedSearch ||
          normalizedValue === normalizedSearch
        );
      });

      if (exactMatch) {
        setStateSearch(exactMatch.label);
        updateSelectedState(exactMatch.value);
        return;
      }

      if (!normalizedSearch) {
        updateSelectedState("");
        return;
      }

      if (!state) {
        setStateSearch("");
      }
    }, 150);
  };

  const handleStateDropdownToggle = (e) => {
    e.preventDefault();
    setIsStateDropdownOpen((prev) => !prev);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFileName(file.name);
      setUploadedFileURL(URL.createObjectURL(file));
      setUniversalFormFile(file);      // ← send file to logic for S3 upload
    } else {
      setUploadedFileName("No file chosen");
      setUploadedFileURL(null);
      setUniversalFormFile(null);
    }
  };
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Block if JSON tab is open and has error
    if (
      entryType === "Request Based PDF Entry" &&
      acroFieldsTab === "json" &&
      jsonInput.trim() &&
      jsonError
    ) {
      return; // JSON error already shown inline
    }

    handleSubmit(e);
  };

  const handleJsonInput = (value) => {
    setJsonInput(value);

    if (!value.trim()) {
      setJsonError("");
      setAcroFieldsList([]);
      return;
    }

    try {
      const parsed = JSON.parse(value);

      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setJsonError("Invalid JSON. Please check your syntax.");
        return;
      }

      setJsonError("");
      const converted = Object.entries(parsed).map(([key, val]) => ({
        key,
        value: String(val),
      }));
      setAcroFieldsList(converted);
    } catch {
      setJsonError("Invalid JSON. Please check your syntax.");
    }
  };

  const syncAcroListToJson = () => {
    const obj = {};

    acroFieldsList.forEach((item) => {
      if (item.key) {
        obj[item.key] = item.value;
      }
    });

    setJsonInput(JSON.stringify(obj, null, 2));
  };

  const syncAcroJsonToList = () => {
    try {
      const parsed = JSON.parse(jsonInput);

      const converted = Object.entries(parsed).map(([key, value]) => ({
        key,
        value: String(value),
      }));

      setAcroFieldsList(converted);
    } catch {
      setAcroFieldsList([]);
    }
  };

  const handleDeleteAcroField = (index) => {
    setAcroFieldsList((prev) =>
      prev.filter((_, idx) => idx !== index)
    );
  };

  return (
    <div className="addUserModal">
      <div className="addUserBody">
        <div className="addUserHeader">
          <span>Add Dataset</span>
          <MdCancel size={24} className="closeAddForm" onClick={onCancel} />
        </div>

        <div className="addUserBodyMain">
          <form onSubmit={handleFormSubmit}>
            <div className="addUsersFields">

              {/* Dataset ID */}
              <div className="form-group inputsSet">
                <label>Dataset ID <span style={{ color: "#dc3545" }}>*</span></label>
                <input
                  type="text"
                  name="datasetID"
                  value={datasetID}
                  onChange={handleChange}
                  className={`form-control addInputUser ${errors.datasetID ? "is-invalid" : ""}`}
                  placeholder="Enter Dataset ID"
                />
                {errors.datasetID && <div className="error-text-Dataset">{errors.datasetID}</div>}
              </div>

              {/* Dataset Name */}
              <div className="form-group inputsSet">
                <label>Dataset Name <span style={{ color: "#dc3545" }}>*</span></label>
                <input
                  type="text"
                  name="datasetName"
                  value={datasetName}
                  onChange={handleChange}
                  className={`form-control addInputUser ${errors.datasetName ? "is-invalid" : ""}`}
                  placeholder="Enter Dataset Name"
                />
                {errors.datasetName && (
                  <div className="error-text-Dataset">{errors.datasetName}</div>
                )}
              </div>

              {/* Display Name */}
              <div className="form-group inputsSet">
                <label>Display Name <span style={{ color: "#dc3545" }}>*</span></label>
                <input
                  type="text"
                  name="datasetDisplayName"
                  value={datasetDisplayName}
                  onChange={handleChange}
                  className={`form-control addInputUser ${errors.datasetDisplayName ? "is-invalid" : ""}`}
                  placeholder="Enter Display Name"
                />
                {errors.datasetDisplayName && (
                  <div className="error-text-Dataset">{errors.datasetDisplayName}</div>
                )}
              </div>

              {/* State */}
              <div className="form-group inputsSet stateFieldWrap">
                <label>State <span style={{ color: "#dc3545" }}>*</span></label>
                <div className="stateInputWrap">
                  <input
                    type="text"
                    value={stateSearch}
                    onChange={handleStateInputChange}
                    onFocus={() => setIsStateDropdownOpen(true)}
                    onBlur={handleStateBlur}
                    className={`custom-select addInputUser stateInputControl ${errors.state ? "is-invalid" : ""}`}
                    placeholder="Enter State Name or Abbreviation"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="stateDropdownToggle"
                    onMouseDown={handleStateDropdownToggle}
                    aria-label="Toggle state dropdown"
                  >
                    <IoChevronDown
                      size={16}
                    // className={isStateDropdownOpen ? "stateChevron open" : "stateChevron"}
                    />
                  </button>
                </div>
                {isStateDropdownOpen && (
                  <div className="stateDropdownMenu">
                    {filteredStateOptions.length > 0 ? (
                      filteredStateOptions.map((stateOption) => (
                        <button
                          key={stateOption.value}
                          type="button"
                          className={`stateDropdownOption ${state === stateOption.value ? "active" : ""}`}
                          onMouseDown={() => handleStateSelect(stateOption)}
                        >
                          <span>{stateOption.label}</span>
                          <span>{stateOption.value}</span>
                        </button>
                      ))
                    ) : (
                      <div className="stateDropdownEmpty">No matching states</div>
                    )}
                  </div>
                )}
                {errors.state && (
                  <div className="error-text-Dataset">{errors.state}</div>
                )}
              </div>

              {/* ENTRY TYPE */}
              <div className="form-group inputsSet" style={{ marginTop: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input type="radio" name="entryType"
                    value="Request Based PDF Entry"
                    checked={entryType === "Request Based PDF Entry"}
                    onChange={handleChange}
                  />
                  <span>Request Based PDF Entry</span>
                </div>
              </div>
              <div className="form-group inputsSet" style={{ marginTop: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input type="radio" name="entryType"
                    value="Counter Based WebSite Entry"
                    checked={entryType === "Counter Based WebSite Entry"}
                    onChange={handleChange}
                  />
                  <span>Counter Based WebSite Entry</span>
                </div>

                {entryType === "Counter Based WebSite Entry" && (
                  <div className="entry-container-edit">
                    <div className="radio-row-edit">
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input type="radio" name="objectType" value="Grouped Object"
                          checked={objectType === "Grouped Object"}
                          onChange={handleChange}
                        />
                        <span>Grouped Object</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input type="radio" name="objectType" value="Individual Object"
                          checked={objectType === "Individual Object"}
                          onChange={handleChange}
                        />
                        <span>Individual Object</span>
                      </div>
                    </div>
                    {errors.objectType && (
                      <div className="error-text-Dataset">{errors.objectType}</div>
                    )}
                  </div>
                )}
              </div>
              <div className="form-group inputsSet" style={{ marginTop: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input type="radio" name="entryType"
                    value="Date Range Based PDF Entry"
                    checked={entryType === "Date Range Based PDF Entry"}
                    onChange={handleChange}
                  />
                  <span>Date Range Based PDF Entry</span>
                </div>

                {entryType === "Date Range Based PDF Entry" && (
                  <div className="entry-container-edit">
                    <div className="radio-row-edit">
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input type="radio" name="objectType" value="Grouped Object"
                          checked={objectType === "Grouped Object"}
                          onChange={handleChange}
                        />
                        <span>Grouped Object</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input type="radio" name="objectType" value="Individual Object"
                          checked={objectType === "Individual Object"}
                          onChange={handleChange}
                        />
                        <span>Individual Object</span>
                      </div>
                    </div>
                    {errors.objectType && (
                      <div className="error-text-Dataset">{errors.objectType}</div>
                    )}
                  </div>
                )}
              </div>

              {/* REQUEST BASED */}
              {entryType === "Request Based PDF Entry" && (
                <>
                  <div className="range-row">
                    {/* <div className="form-group inputsSet">
                      <label>Zoho Department ID</label>
                      <input
                        type="text"
                        name="zohoDepartmentID"
                        value={zohoDepartmentID}
                        onChange={handleChange}
                        className="form-control addInputUser"
                        placeholder="Enter Zoho Department ID"
                      />
                    </div> */}
                  </div>
                  <div className="global-requestor-header">
                    <h4>Global Requestor Information</h4>
                  </div>
                  {/* Form Upload */}
                  <div
                    className="smtp-settings-container container-smtp"
                    style={{ marginTop: "8px" }}
                  >
                    <h5>Form Upload</h5>

                    {/* UPLOAD + BUTTONS ROW */}
                    <div className="universal-upload-row">

                      {/* FILE INPUT */}
                      <div className="universal-upload-container">

                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleFileChange}
                          className="form-control addInputUser"
                        />

                        {uploadedFileURL && (
                          <FaEye
                            size={18}
                            className="upload-preview-icon"
                            onClick={() => window.open(uploadedFileURL, "_blank")}
                          />
                        )}

                      </div>

                      {/* JSON / LIST BUTTONS */}
                      <div className="dsjson-list-tabs1 upload-tabs">

                        <button
                          type="button"
                          className={`dstab-btn1 ${acroFieldsTab === "json" ? "active" : ""}`}
                          onClick={() => {
                            syncAcroListToJson();
                            setAcroFieldsTab("json");
                            setShowAcroFields(true);
                          }}
                        >
                          JSON
                        </button>

                        <button
                          type="button"
                          className={`dstab-btn1 ${acroFieldsTab === "list" ? "active" : ""}`}
                          onClick={() => {
                            syncAcroJsonToList();
                            setAcroFieldsTab("list");
                            setShowAcroFields(true);
                          }}
                        >
                          LIST
                        </button>

                      </div>

                    </div>

                    {/* ACCORDION */}
                    <div className="accordion-item1 mt-3">

                      <div
                        className="ds-accordion-header"
                        onClick={() => setShowAcroFields(!showAcroFields)}
                      >
                        <span>
                          {acroFieldsTab === "json"
                            ? "AcroFields JSON"
                            : "AcroFields List"}
                        </span>

                        <span>
                          {showAcroFields
                            ? <IoIosArrowUp />
                            : <IoIosArrowDown />}
                        </span>
                      </div>

                      {showAcroFields && (

                        <div className="accordion-content">

                          {/* JSON VIEW */}
                          {acroFieldsTab === "json" && (
                            <div className="form-group input-section">
                              <textarea
                                className="form-control json-textarea"
                                placeholder='Enter JSON Object'
                                value={jsonInput}
                                onChange={(e) => handleJsonInput(e.target.value)}
                                rows={6}
                              />
                              {jsonError && (
                                <div className="error-json-Dataset">
                                  {jsonError}
                                </div>
                              )}
                            </div>
                          )}

                          {/* LIST VIEW */}
                          {acroFieldsTab === "list" && (
                            <div>

                              <div className="dslist-header-row1">
                                <span
                                  className="add-item-btn1"
                                  onClick={() => {
                                    setAcroFieldsList((prev) => [
                                      { key: "", value: "" },
                                      ...prev,
                                    ]);
                                  }}
                                >
                                  <FaPlus size={16} />
                                  Add Field
                                </span>
                              </div>

                              <table className="dslist-table1">
                                <thead>
                                  <tr>
                                    <th>Field Name</th>
                                    <th>Field Value</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {acroFieldsList.map((row, index) => (
                                    <tr key={index}>
                                      <td>
                                        <input
                                          className="form-control list-input1"
                                          value={row.key}
                                          onChange={(e) => {
                                            const updated = [...acroFieldsList];
                                            updated[index].key = e.target.value;
                                            setAcroFieldsList(updated);
                                          }}
                                        />
                                      </td>

                                      <td>
                                        <input
                                          className="form-control list-input1"
                                          value={row.value}
                                          onChange={(e) => {
                                            const updated = [...acroFieldsList];
                                            updated[index].value = e.target.value;
                                            setAcroFieldsList(updated);
                                          }}
                                        />
                                      </td>

                                      <td>
                                        <RiDeleteBin3Fill
                                          style={{
                                            color: "#dc3545",
                                            cursor: "pointer",
                                          }}
                                          size="16px"
                                          onClick={() => handleDeleteAcroField(index)}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>

                              </table>

                            </div>
                          )}

                        </div>
                      )}

                    </div>
                  </div>

                  <div className="smtp-settings-container container-smtp" style={{ marginTop: "8px" }}>
                    <h5>SMTP Settings</h5>

                    <div className="form-group smtp-from-AD">
                      <label>From Email <span style={{ color: "#dc3545" }}>*</span></label>
                      <input
                        type="text"
                        name="smtpFromEmail"
                        value={smtpFromEmail}
                        onChange={handleChange}
                        className={`form-control addInputUser ${errors.smtpFromEmail ? "is-invalid" : ""}`}
                        placeholder="Enter From Email"
                      />
                      {errors.smtpFromEmail && <div className="error-text-Dataset">{errors.smtpFromEmail}</div>}
                    </div>

                    <div className="form-group smtp-from-AD">
                      <label>Reply To Email <span style={{ color: "#dc3545" }}>*</span></label>
                      <input
                        type="text"
                        name="replyToEmail"
                        value={replyToEmail}
                        onChange={handleChange}
                        className={`form-control addInputUser ${errors.replyToEmail ? "is-invalid" : ""}`}
                        placeholder="Enter Reply To Email"
                      />
                      {errors.replyToEmail && <div className="error-text-Dataset">{errors.replyToEmail}</div>}
                    </div>

                    <div className="form-group smtp-from-AD">
                      <label>Subject</label>
                      <input
                        type="text"
                        name="smtpSubject"
                        value={smtpSubject}
                        onChange={handleChange}
                        // className={`form-control addInputUser ${errors.smtpSubject ? "is-invalid" : ""}`}
                        className="form-control addInputUser"
                        placeholder="Enter Subject"
                      />
                      {/* {errors.smtpSubject && <div className="error-text-Dataset">{errors.smtpSubject}</div>} */}
                    </div>
                  </div>

                  {/* THRESHOLDS */}
                  <div className="ds-accordion-item mt-3">
                    <div
                      className="ds-accordion-header"
                      onClick={() => setShowThresholds(!showThresholds)}
                    >
                      <span>Thresholds</span>
                      <span>{showThresholds ? <IoIosArrowUp /> : <IoIosArrowDown />}</span>
                    </div>

                    {showThresholds && (
                      <div className="ds-accordion-content">
                        <div className="ds-thresholds-fields">

                          <div className="ds-threshold-group">
                            <label>Daily Entry Limit</label>
                            <input
                              type="number"
                              name="dailyEntryLimit"
                              value={dailyEntryLimit}
                              min={0}
                              onChange={handleChange}
                              className="form-control addInputUser"
                              placeholder="Enter Daily Entry Limit"
                            />
                          </div>

                          <div className="ds-threshold-group">
                            <label>Days Allowed for QA Review</label>
                            <input
                              type="number"
                              name="qaCompletionDays"
                              value={qaCompletionDays}
                              min={0}
                              max={9}
                              onChange={handleChange}
                              className="form-control addInputUser"
                              placeholder="Enter Days Allowed for QA Review"
                            />
                          </div>

                          <div className="ds-threshold-group">
                            <label>Days Allowed to Approve Ignored Files/PDFs</label>
                            <input
                              type="number"
                              name="ignoredFileApprovalDays"
                              value={ignoredFileApprovalDays}
                              min={0}
                              max={9}
                              onChange={handleChange}
                              className="form-control addInputUser"
                              placeholder="Enter Days Allowed to Approve Ignored Files/PDFs"
                            />
                          </div>

                        </div>
                      </div>
                    )}
                  </div>
                  {/* OPRA Request Send Enabled */}
                  <div className="opra-checkbox-row">
                    <input
                      type="checkbox"
                      id="datasetOPRARequestSendEnable"
                      checked={datasetOPRARequestSendEnable}
                      onChange={(e) =>
                        handleChange({ target: { name: "datasetOPRARequestSendEnable", checked: e.target.checked } })
                      }
                    />
                    <label htmlFor="datasetOPRARequestSendEnable">
                      OPRA Request Send Enabled
                    </label>
                  </div>
                </>
              )}

            </div>

            <div className="button-right-add">
              <button type="submit" className="add-button-user">
                Save Dataset
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default AddDataset;